import { Close } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Box,
  IconButton,
  Typography,
} from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import Color from "color";
import { useEffect, useState } from "react";

import { useCompetitionQuery, useCurrentCompetitionUuid } from "@/hooks/competitions";
import { useCompetitionRouteLookup } from "@/hooks/routes";

import { useCompetitionContext } from "../CompetitionOverview/CompetitionProvider";
import { useModal } from "../modals/useModal";
import { CircuitChip } from "../RoutesTable/CircuitCell";
import { RouteNameText } from "../RoutesTable/RouteName";
import Sidebar, { SidebarHeader } from "../Sidebar";
import { CompetitionRouteAttempts } from "./CompetitionRouteAttempts";
import RouteCategoryForm from "./RouteCategoryEditor";
import RouteCustomScoringForm from "./RouteCustomScoringForm";

export default function CompetitionRouteSidebar({ routeUuid }: { routeUuid: string }) {
  const competitionUuid = useCurrentCompetitionUuid();
  const route = useCompetitionRouteLookup(competitionUuid, routeUuid);

  const { data: competition } = useCompetitionQuery(competitionUuid);
  const { categoryEditorIsDirty } = useCompetitionContext();

  let fgColor: string, bgColor: string;

  if (route) {
    const colors = (route.colors || "#FFFFFF").split(":");
    bgColor = colors[0];
    fgColor = Color(bgColor).isDark() ? "#FFFFFF" : "#000000";
  } else {
    bgColor = "#FFFFFF";
    fgColor = "#000000";
  }

  const [activeSection, setActiveSection] = useState<"categories" | "attempts" | "customScoring">(
    "categories",
  );

  useEffect(() => {
    const status = competition?.status;
    if (status && status !== "UPCOMING") {
      setActiveSection("attempts");
    }
  }, [competition?.status]);

  const navigate = useNavigate();
  const { display: displayModal } = useModal();

  const onTryClose = () => {
    if (categoryEditorIsDirty) {
      displayModal("UNSAVED_COMPETITION_CATEGORY_CHANGES_PROMPT");
    } else {
      navigate({
        to: "/gym/$venueUuid/events/$competitionUuid/routes",
        from: "/gym/$venueUuid/events/$competitionUuid/routes/$routeUuid",
        replace: true,
        params: (prev) => {
          const { routeUuid: _, ...params } = prev;
          return params;
        },
      });
    }
  };

  return (
    <Box sx={{ backgroundColor: "primary.blue2" }}>
      <Sidebar
        sx={{
          overflow: "hidden",
          borderRadius: "16px 16px 0 0",
          boxShadow: "-1px -1px 0 #00000044, -1px -1px 10px #00000022",
        }}
      >
        <SidebarHeader
          sx={{
            backgroundColor: bgColor,
            color: fgColor,
            padding: 1,
            fontSize: 16,
            fontWeight: "bold",
            borderBottom: "1px solid",
            borderBottomColor: "#00000033",
          }}
        >
          {route && !competition?.settings?.hasDynamicRouteNumbering ? (
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: fgColor,
              }}
            >
              <RouteNameText route={route} />
            </Typography>
          ) : null}
          {route && competition?.settings?.hasDynamicRouteNumbering ? (
            <Box>
              {route.competitionRouteGroups.map((crg) => {
                const category = competition.stages[0].categories.find(
                  (x) => x.uuid === crg.categories[0],
                );
                const routeDisplay = route.displayRouteGroupMapping[crg.uuid];
                return (
                  <>
                    <CircuitChip
                      key={crg.uuid}
                      label={category?.name + (routeDisplay ? ` #${routeDisplay.number}` : " ?")}
                      colors={[category?.color || "#000000"]}
                    />{" "}
                  </>
                );
              })}
            </Box>
          ) : null}
          <IconButton
            size="sm"
            sx={{
              marginBottom: -0.5,
              borderRadius: 100,
              "&:hover": {
                backgroundColor: fgColor + "33",
              },
            }}
            onClick={onTryClose}
          >
            <Close sx={{ color: fgColor }} />
          </IconButton>
        </SidebarHeader>
        <AccordionGroup
          sx={{
            flex: 1,
            overflow: "hidden",
            ".MuiAccordionDetails-root": {
              flex: 1,
            },
            ".MuiAccordionDetails-content": {
              padding: 0,
            },
            ".MuiAccordion-root.Mui-expanded": {
              flex: 1,
            },
            ".MuiAccordionSummary-root": {
              fontWeight: "bold",
              backgroundColor: "neutral.100",
            },
          }}
        >
          <Accordion
            expanded={activeSection === "categories"}
            onChange={(_e, expanded) => setActiveSection(expanded ? "categories" : "attempts")}
          >
            <AccordionSummary>Setup</AccordionSummary>
            <AccordionDetails>
              <RouteCategoryForm route={route} competition={competition} key={route?.uuid} />
            </AccordionDetails>
          </Accordion>
          {competition?.scoringSystem === "CUSTOM" ? (
            <Accordion
              expanded={activeSection === "customScoring"}
              onChange={(_e, expanded) =>
                setActiveSection(expanded ? "customScoring" : "categories")
              }
            >
              <AccordionSummary>Custom scoring</AccordionSummary>
              <AccordionDetails>
                <RouteCustomScoringForm route={route} competition={competition} />
              </AccordionDetails>
            </Accordion>
          ) : null}

          {competition?.status !== "UPCOMING" && route?.startTime ? (
            <Accordion
              expanded={activeSection === "attempts"}
              onChange={(_e, expanded) => setActiveSection(expanded ? "attempts" : "categories")}
            >
              <AccordionSummary>Attempts</AccordionSummary>
              <AccordionDetails>
                <CompetitionRouteAttempts route={route} competition={competition} />
              </AccordionDetails>
            </Accordion>
          ) : null}
        </AccordionGroup>
      </Sidebar>
    </Box>
  );
}
